import GokuDataGrid from '@/components/GokuDataGrid'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useTranslation } from 'react-i18next'
import { getServiceAnalyticMonitorings } from '../../services'
import { AnalyticQuery } from '../../types'
import {
    analyzeDataTrends,
    buildAnalyticsData,
    createUnitConversions,
    DataBuilderConfig,
    formatPeriodKey,
    getAllUsageKeys,
    getAllHierarchicalKeys,
    getAllProviders,
    getColumnHeaders,
} from '../../utils/functions'
import AnalyticsSummaryPaper from '../AnalyticsSummary'
import { generateAnalyticsColumns } from '../columns'
import { DisplayConfig } from '../index'
import React from 'react'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

// Generate column grouping model for periods
const generateColumnGroupingModel = (
    data: any[],
    config: DataBuilderConfig,
    productGroupName?: string
): any => {
    if (!data || data.length === 0) return []

    const periods = getColumnHeaders(data, config)
    const isVNIS = productGroupName === 'VNIS'
    const keys = isVNIS ? getAllHierarchicalKeys(data) : getAllUsageKeys(data)

    if (isVNIS && config.timePeriod === 'weekly') {
        // For VNIS weekly, group by base usage keys first, then providers
        const baseUsageKeys = getAllUsageKeys(data)

        return periods.map((periodKey) => ({
            groupId: periodKey,
            headerName: formatPeriodKey(periodKey, config.timePeriod),
            headerAlign: 'center' as const,
            children: baseUsageKeys.map((baseKey) => {
                // Get all hierarchical keys for this base usage key
                const hierarchicalKeysForBase = keys.filter((key) =>
                    key.startsWith(baseKey + '.')
                )

                if (hierarchicalKeysForBase.length > 0) {
                    // Create sub-group for this usage key
                    return {
                        groupId: `${periodKey}_${baseKey}`,
                        headerName: baseKey,
                        headerAlign: 'center' as const,
                        children: hierarchicalKeysForBase.map(
                            (hierarchicalKey) => ({
                                field: `${periodKey}_${hierarchicalKey}`,
                            })
                        ),
                    }
                } else {
                    // No hierarchical keys, use base key
                    return {
                        field: `${periodKey}_${baseKey}`,
                    }
                }
            }),
        }))
    } else {
        // Standard grouping for non-VNIS
        return periods.map((periodKey) => ({
            groupId: periodKey,
            headerName: formatPeriodKey(periodKey, config.timePeriod),
            headerAlign: 'center' as const,
            children: keys.map((usageKey) => ({
                field: `${periodKey}_${usageKey}`,
            })),
        }))
    }
}

interface DataVisualizationProps {
    query: AnalyticQuery
    analyticsConfig: Omit<DataBuilderConfig, 'startDate' | 'endDate'>
    displayConfig: DisplayConfig
}
const DataVisualization = React.memo(
    ({ query, analyticsConfig, displayConfig }: DataVisualizationProps) => {
        const { t } = useTranslation()
        const { data, renderState } = useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.SERVICES_ANALYTICS_MONITORING,
                query,
                query.product_group_name,
                query.account_manager_codes?.join(',') || '',
                query.sale_manager_codes?.join(',') || '',
                analyticsConfig.timePeriod,
                analyticsConfig.aggregationMethod,
                analyticsConfig.usageKeys?.join(',') || '',
                displayConfig.showAllProviders.toString(),
                displayConfig.selectedProviders?.join(',') || '',
            ],
            queryFn: () => getServiceAnalyticMonitorings(query),
        })

        const stateComponent = renderState()
        if (stateComponent) return stateComponent

        // Extract available providers from data and notify parent
        if (data?.results && query.product_group_name === 'VNIS') {
            const providers = getAllProviders(data.results)
            displayConfig.providers = providers
        }

        // // Build analytics data with unit conversions
        const config: DataBuilderConfig = {
            timePeriod: analyticsConfig.timePeriod,
            aggregationMethod: analyticsConfig.aggregationMethod,
            startDate: query.gte || '',
            endDate: query.lte || '',
            unitConversions: createUnitConversions(),
            usageKeys: analyticsConfig.usageKeys,
        }

        const builtData = buildAnalyticsData(
            data?.results || [],
            config,
            query.product_group_name,
            displayConfig.showAllProviders,
            displayConfig.selectedProviders
        )

        // Generate analytics summary
        const analyticsSummary = analyzeDataTrends(builtData, config, t)

        // Generate column grouping model
        const columnGroupingModel = generateColumnGroupingModel(
            builtData,
            config,
            query.product_group_name
        )

        return (
            <div className="space-y-2">
                <GokuDataGrid
                    reactQueryKey={[
                        'service-analytic-monitorings-built',
                        query.gte,
                        query.lte,
                        query.account_manager_codes?.join(',') || '',
                        query.sale_manager_codes?.join(',') || '',
                        query.product_group_name,
                        query.q,
                        config.timePeriod,
                        config.aggregationMethod,
                        config.usageKeys?.join(',') || '',
                        displayConfig.mode,
                        displayConfig.showOriginalValues,
                        displayConfig.showGrowthIndicators,
                        displayConfig.showConversionInfo,
                        displayConfig.showAllProviders,
                        displayConfig.selectedProviders?.join(',') || '',
                    ].join('-')}
                    columns={generateAnalyticsColumns(
                        builtData,
                        config,
                        displayConfig,
                        t,
                        query.product_group_name
                    )}
                    // Chế độ two-line xếp 2 dòng trong một ô (giá trị và
                    // "≈ ... <đơn vị gốc>"), cao hơn mức mặc định 18+32=50px của
                    // grid nên nửa dưới bị cắt. Nhân đôi giống WorkSchedule.
                    getRowHeight={(_, defaultHeight) =>
                        displayConfig.mode === 'two-line'
                            ? defaultHeight * 2
                            : defaultHeight
                    }
                    options={{
                        title: undefined,
                        showHideColumn: false,
                        showSearch: false,
                        pageSize: 10,
                    }}
                    dataGridProps={{
                        getRowId: (row) => row?.customer_id,
                        columnGroupingModel: columnGroupingModel,
                        pinnedColumns: {
                            left: [
                                'customer_id',
                                'customer_name',
                                'service_entity_name',
                            ],
                        },
                        sx: {
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 600,
                            },
                            '& .MuiDataGrid-columnGroupHeader': {
                                backgroundColor: '#f8fafc',
                                borderBottom: '2px solid #e2e8f0',
                                fontWeight: 700,
                                fontSize: '14px',
                            },
                            '& .MuiDataGrid-columnHeader': {
                                backgroundColor: '#ffffff',
                                borderRight: '1px solid #e2e8f0',
                            },
                            '& .MuiDataGrid-cell': {
                                borderRight: '1px solid #f1f5f9',
                            },
                        },
                    }}
                    apiCall={async () => {
                        return {
                            results: builtData,
                            total: builtData.length,
                            page: 1,
                            page_size: 10,
                        }
                    }}
                    paginationMode="client"
                />
                <AnalyticsSummaryPaper
                    summary={analyticsSummary}
                    data={builtData}
                    config={config}
                />
            </div>
        )
    }
)

export default DataVisualization
